<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  deals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const isEmpty = computed(() => props.deals.length === 0);

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatAmount = amount => {
  if (amount == null) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const statusBadgeClass = status => {
  const classes = {
    open: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    won: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };
  return (
    classes[status] ||
    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  );
};

const statusTooltip = computed(() => {
  return [
    `${t('DEALS.STATUS.OPEN')}: ${t('DEALS.STATUS.DESCRIPTION.OPEN')}`,
    `${t('DEALS.STATUS.WON')}: ${t('DEALS.STATUS.DESCRIPTION.WON')}`,
    `${t('DEALS.STATUS.LOST')}: ${t('DEALS.STATUS.DESCRIPTION.LOST')}`,
  ].join('\n');
});

const handleEdit = deal => emit('edit', deal);
const handleDelete = deal => emit('delete', deal);
</script>

<template>
  <div class="w-full">
    <div
      v-if="isEmpty"
      class="flex items-center justify-center py-12 text-sm text-slate-500 dark:text-slate-400"
    >
      {{ t('DEALS.TABLE.EMPTY') }}
    </div>

    <table v-else class="w-full text-sm text-left border-collapse">
      <thead>
        <tr class="border-b border-slate-200 dark:border-slate-700">
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.NAME') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.AMOUNT') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.STAGE') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.CONTACT') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.ASSIGNEE') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            {{ t('DEALS.TABLE.COLUMNS.EXPECTED_CLOSE_DATE') }}
          </th>
          <th class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">
            <span class="inline-flex items-center gap-1">
              {{ t('DEALS.TABLE.COLUMNS.STATUS') }}
              <i
                class="i-lucide-info size-3.5 text-slate-400"
                :title="statusTooltip"
              />
            </span>
          </th>
          <th
            class="px-4 py-3 font-medium text-slate-600 dark:text-slate-300 text-right"
          >
            {{ t('DEALS.TABLE.COLUMNS.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="deal in deals"
          :key="deal.id"
          class="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <td class="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
            {{ deal.name }}
          </td>
          <td class="px-4 py-3 text-slate-700 dark:text-slate-300">
            {{ formatAmount(deal.amount) }}
          </td>
          <td class="px-4 py-3 text-slate-700 dark:text-slate-300">
            {{ deal.stage?.name || '-' }}
          </td>
          <td class="px-4 py-3 text-slate-700 dark:text-slate-300">
            {{ deal.contact?.name || '-' }}
          </td>
          <td class="px-4 py-3 text-slate-700 dark:text-slate-300">
            {{ deal.assignee?.name || '-' }}
          </td>
          <td class="px-4 py-3 text-slate-700 dark:text-slate-300">
            {{ formatDate(deal.expectedCloseDate) }}
          </td>
          <td class="px-4 py-3">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              :class="statusBadgeClass(deal.status)"
            >
              {{ t(`DEALS.STATUS.${(deal.status || 'open').toUpperCase()}`) }}
            </span>
          </td>
          <td class="px-4 py-3">
            <div class="flex items-center justify-end gap-2">
              <button
                class="p-1 text-slate-400 hover:text-woot-500 transition-colors"
                :title="t('DEALS.TABLE.ACTIONS.EDIT')"
                @click="handleEdit(deal)"
              >
                <i class="i-lucide-pencil size-4" />
              </button>
              <button
                class="p-1 text-slate-400 hover:text-red-500 transition-colors"
                :title="t('DEALS.TABLE.ACTIONS.DELETE')"
                @click="handleDelete(deal)"
              >
                <i class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
